import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
    ICreateComparisonBoardRequest,
} from '../../../../interfaces/comparison-boards/IComparisonBoards';
import Modal from '../../../../shared/modal';
import {
    IComparisonBoardClientOption,
    IComparisonBoardPropertyOption,
} from '../../../../store/users/usersApi';

interface CreateComparisonBoardForm {
    client1: string;
    client2: string;
    clone: boolean;
}

const initialForm: CreateComparisonBoardForm = {
    client1: '',
    client2: '',
    clone: false,
};

const inputClassName = 'w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:border-gray-300 dark:focus:border-gray-600';

const normalizeName = (value: string) => value.trim().toLowerCase();

const getClientValue = (client: IComparisonBoardClientOption) =>
    client.username || client.id;

const getClientProperties = (
    properties: IComparisonBoardPropertyOption[],
    client?: IComparisonBoardClientOption,
) => {
    if (!client?.companySub) return [];

    const map = new Map<string, IComparisonBoardPropertyOption>();
    properties
        .filter((property) => property.companySub === client.companySub)
        .forEach((property) => {
            const key = normalizeName(property.propertyName);
            if (key && !map.has(key)) {
                map.set(key, property);
            }
        });

    return Array.from(map.values()).sort((a, b) =>
        a.propertyName.localeCompare(b.propertyName, undefined, { sensitivity: 'base' }));
};

const haveMatchingProperties = (
    client1Properties: IComparisonBoardPropertyOption[],
    client2Properties: IComparisonBoardPropertyOption[],
) => {
    const first = client1Properties
        .map((property) => normalizeName(property.propertyName))
        .filter(Boolean)
        .sort();
    const second = client2Properties
        .map((property) => normalizeName(property.propertyName))
        .filter(Boolean)
        .sort();

    if (first.length !== second.length) return false;
    return first.every((name, index) => name === second[index]);
};

const CreateComparisonBoardModal = ({
    open,
    loading,
    loadingClients,
    loadingProperties,
    error,
    clients,
    properties,
    onClose,
    onSubmit,
}: {
    open: boolean;
    loading?: boolean;
    loadingClients?: boolean;
    loadingProperties?: boolean;
    error?: string | null;
    clients: IComparisonBoardClientOption[];
    properties: IComparisonBoardPropertyOption[];
    onClose: () => void;
    onSubmit: (payload: ICreateComparisonBoardRequest) => Promise<boolean>;
}) => {
    const [form, setForm] = useState<CreateComparisonBoardForm>(initialForm);
    const [websiteUrls, setWebsiteUrls] = useState<Record<string, string>>({});
    const [touchedSubmit, setTouchedSubmit] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(initialForm);
            setWebsiteUrls({});
            setTouchedSubmit(false);
        }
    }, [open]);

    const selectedClient1 = useMemo(
        () => clients.find((client) => getClientValue(client) === form.client1),
        [clients, form.client1],
    );

    const selectedClient2 = useMemo(
        () => clients.find((client) => getClientValue(client) === form.client2),
        [clients, form.client2],
    );

    const client1Properties = useMemo(
        () => getClientProperties(properties, selectedClient1),
        [properties, selectedClient1],
    );

    const client2Properties = useMemo(
        () => getClientProperties(properties, selectedClient2),
        [properties, selectedClient2],
    );

    const propertyNamesMatch = useMemo(
        () => form.clone || !selectedClient2 || haveMatchingProperties(client1Properties, client2Properties),
        [client1Properties, client2Properties, form.clone, selectedClient2],
    );

    const comparisonError = selectedClient1 && selectedClient2 && !form.clone && !propertyNamesMatch
        ? "These clients can't be compared because their properties do not fully match."
        : '';

    const propertiesToShow = selectedClient1 && (form.clone || selectedClient2) && !comparisonError
        ? client1Properties
        : [];

    const validationErrors = useMemo(() => {
        const errors: Record<string, string> = {};

        if (!form.client1) {
            errors.client1 = 'Source client is required.';
        }

        if (!form.clone && !form.client2) {
            errors.compareMode = 'Choose clone or select a comparison client.';
        }

        if (!form.clone && form.client1 && form.client2 && form.client1 === form.client2) {
            errors.compareMode = 'Select a different client or use clone.';
        }

        if (comparisonError) {
            errors.compareMode = comparisonError;
        }

        if (
            selectedClient1
            && (form.clone || selectedClient2)
            && !comparisonError
            && !loadingProperties
            && propertiesToShow.length === 0
        ) {
            errors.properties = 'No finished setup properties found for the selected client.';
        }

        return errors;
    }, [
        comparisonError,
        form.client1,
        form.client2,
        form.clone,
        loadingProperties,
        propertiesToShow.length,
        selectedClient1,
        selectedClient2,
    ]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const updateForm = <K extends keyof CreateComparisonBoardForm>(
        field: K,
        value: CreateComparisonBoardForm[K],
    ) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const onClient1Change = (value: string) => {
        setWebsiteUrls({});
        setForm((prev) => ({
            ...prev,
            client1: value,
            client2: prev.client2 === value ? '' : prev.client2,
        }));
    };

    const onClient2Change = (value: string) => {
        setWebsiteUrls({});
        updateForm('client2', value);
    };

    const onToggleClone = () => {
        setWebsiteUrls({});
        setForm((prev) => ({
            ...prev,
            clone: !prev.clone,
            client2: prev.clone ? prev.client2 : '',
        }));
    };

    const resetAndClose = () => {
        if (loading) return;
        setForm(initialForm);
        setWebsiteUrls({});
        setTouchedSubmit(false);
        onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setTouchedSubmit(true);
        if (hasValidationErrors || loading || loadingClients || loadingProperties || !selectedClient1) return;

        const payload: ICreateComparisonBoardRequest = {
            client1: selectedClient1.username,
            ...(form.clone
                ? { clone: true as const }
                : selectedClient2
                    ? { client2: selectedClient2.username }
                    : {}),
            websites: propertiesToShow.map((property) => ({
                property: property.propertyName,
                url: (websiteUrls[property.propertyName] || '').trim(),
            })),
        };

        const created = await onSubmit(payload);
        if (!created) return;

        setForm(initialForm);
        setWebsiteUrls({});
        setTouchedSubmit(false);
    };

    const showError = (key: string) => touchedSubmit && validationErrors[key];

    return (
        <Modal
            open={open}
            onClose={resetAndClose}
            title='Add Comparison Board'
            contentContainerStyle='w-full max-w-[760px] !max-h-[90vh] overflow-y-auto'
        >
            <form className='pt-2 pb-1' onSubmit={handleSubmit}>
                <div className='grid grid-cols-1 gap-4'>
                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Source Client
                        </label>
                        <select
                            value={form.client1}
                            onChange={(event) => onClient1Change(event.target.value)}
                            disabled={loadingClients}
                            className={inputClassName}
                        >
                            <option value=''>
                                {loadingClients ? 'Loading clients...' : 'Select client'}
                            </option>
                            {clients.map((client) => (
                                <option key={`${client.id}-${client.companySub}`} value={getClientValue(client)}>
                                    {client.label}
                                </option>
                            ))}
                        </select>
                        {showError('client1') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>
                                {validationErrors.client1}
                            </p>
                        )}
                    </div>

                    <div>
                        <p className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Board Setup
                        </p>
                        <div className='grid grid-cols-1 gap-3 md:grid-cols-[160px_40px_minmax(0,1fr)] md:items-start'>
                            <button
                                type='button'
                                onClick={onToggleClone}
                                disabled={!form.client1}
                                className={`h-[40px] rounded-xl border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                    form.clone
                                        ? 'border-[#00330f] bg-[#00330f] text-white dark:border-white dark:bg-white dark:text-black'
                                        : 'border-gray-200 text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode'
                                }`}
                            >
                                Clone Source
                            </button>

                            <div className='flex h-[40px] items-center justify-center text-xs font-semibold uppercase text-gray-500 dark:text-gray-400'>
                                OR
                            </div>

                            <div>
                                <select
                                    value={form.client2}
                                    onChange={(event) => onClient2Change(event.target.value)}
                                    disabled={form.clone || !form.client1 || loadingClients}
                                    className={inputClassName}
                                >
                                    <option value=''>
                                        {form.clone ? 'Clone selected' : 'Select comparison client'}
                                    </option>
                                    {clients
                                        .filter((client) => getClientValue(client) !== form.client1)
                                        .map((client) => (
                                            <option key={`${client.id}-${client.companySub}`} value={getClientValue(client)}>
                                                {client.label}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        {showError('compareMode') && (
                            <p className='mt-2 text-xs text-red-600 dark:text-red-400'>
                                {validationErrors.compareMode}
                            </p>
                        )}
                        {!touchedSubmit && comparisonError && (
                            <p className='mt-2 text-xs text-red-600 dark:text-red-400'>
                                {comparisonError}
                            </p>
                        )}
                    </div>
                </div>

                {selectedClient1 && (form.clone || selectedClient2) && !comparisonError && (
                    <div className='mt-5 rounded-xl border border-gray-200 dark:border-borderColor-darkMode p-3'>
                        <div className='mb-3 flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-[#101010] dark:text-primary-darkMode'>
                                Properties
                            </p>
                            <span className='text-xs text-gray-500 dark:text-gray-400'>
                                {propertiesToShow.length} total
                            </span>
                        </div>

                        {loadingProperties ? (
                            <div className='space-y-3 animate-pulse'>
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <div key={index} className='h-10 rounded-xl bg-gray-100 dark:bg-white/10' />
                                ))}
                            </div>
                        ) : propertiesToShow.length > 0 ? (
                            <div className='grid grid-cols-1 gap-3'>
                                {propertiesToShow.map((property) => (
                                    <div key={property.propertyName} className='grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,240px)_1fr] md:items-center'>
                                        <p className='text-sm font-medium text-[#101010] dark:text-primary-darkMode break-words'>
                                            {property.propertyName}
                                        </p>
                                        <input
                                            value={websiteUrls[property.propertyName] || ''}
                                            onChange={(event) =>
                                                setWebsiteUrls((prev) => ({
                                                    ...prev,
                                                    [property.propertyName]: event.target.value,
                                                }))}
                                            placeholder='https://website.com'
                                            type='url'
                                            className={inputClassName}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className='text-sm text-gray-500 dark:text-gray-400'>
                                No finished setup properties found for the selected client.
                            </p>
                        )}
                    </div>
                )}

                {showError('properties') && (
                    <p className='mt-3 text-xs text-red-600 dark:text-red-400'>
                        {validationErrors.properties}
                    </p>
                )}

                {error && (
                    <p className='mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300'>
                        {error}
                    </p>
                )}

                <div className='mt-6 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-bgColor-darkMode py-3'>
                    <button
                        type='button'
                        onClick={resetAndClose}
                        disabled={loading}
                        className='rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode disabled:opacity-50'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={loading || loadingClients || loadingProperties}
                        className='rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90 disabled:opacity-50'
                    >
                        {loading ? 'Creating...' : 'Create Board'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateComparisonBoardModal;
